import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { Subscription } from 'rxjs';
import { Validators } from '@angular/forms';
import {
	DadosRelatorioPluviometricoDiario,
	PostosRelatorios,
	Relatorios,
} from '@home/submodulos/dados-meteorologicos/interfaces/tabela-relatorio';
import { Estacao } from '@home/submodulos/dados-meteorologicos/interfaces/filtros-opcoes';
import { formatISO } from 'date-fns';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import {
	alinharDado,
	baixarEAbrirPDF,
	getTracoIfNull,
	numberToBrNumber,
	renderCoordenada,
} from '@utils';
import { ToastrService } from 'ngx-toastr';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Select } from '@layout/interfaces/select';
import * as pdfseira from '@utils/pdf-seira';
import { DocumentExporter } from '@utils/document-exporter';
import { DateTimeUtils } from '@utils/datetime-util';
import { corrigeDuplicacaoNome } from '../../../utils';
import * as ExcelTable from 'mr-excel';
import { TipoTabela } from '../../../interfaces';

@Component({
	selector: 'seira-pluviometro-tabela-pluv-diaria-personalizada',
	templateUrl: './pluviometro-tabela-pluv-diaria-personalizada.component.html',
	styleUrls: ['./pluviometro-tabela-pluv-diaria-personalizada.component.scss'],
})
export class PluviometroTabelaPrecipitacaoDiariaComponent
	implements OnInit, OnDestroy, InstanciaRelatorio
{
	descricaoRelatorio =
		'Define-se como pluviometria diária personalizada, a quantidade de chuva acumulada em um determinado local para um período de 24 horas.';
	tituloRelatorio = TipoTabela.PRECIPITACAO_DIARIA + ' (mm)';
	inputs = inject(INPUTS_RELATORIOS);
	postos: PostosRelatorios[] = [];
	estacoes: Estacao[] = [];
	microrregioes!: Select<string>[];
	carregandoRelatorio = true;
	dadosTabelaRelatorio: Relatorios<DadosRelatorioPluviometricoDiario>[] = [];
	opcoesColunas = [
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },
	];
	colunasSelecionadas: string[] = [];
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => {
				return this.exportarPDF(this.dadosTabelaRelatorio);
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => {
				return this.exportarCSV(this.dadosTabelaRelatorio);
			},
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => {
				return this.exportarTXT(this.dadosTabelaRelatorio);
			},
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	private subscription = new Subscription();
	constructor(
		private relatoriosService: RelatoriosService,
		private toastr: ToastrService
	) {
		this.setValidators();
	}

	ngOnInit() {
		this.postos = this.inputs.postos;
		this.estacoes = this.inputs.estacoes;
	}

	getFormItemValue(field: string) {
		return this.inputs.form.get(field)!.value;
	}

	setColunasSelecionadas(colunas: string[]) {
		this.colunasSelecionadas = colunas;
	}

	gerarRelatorio() {
		if (this.inputs.form.invalid) {
			return;
		}
		const dataInicio = this.inputs.form.get('dataInicio')?.value;
		const dataFim = this.inputs.form.get('dataFim')?.value;
		if (!dataInicio || !dataFim || !this.postos.length) {
			return;
		}
		this.inputs.setLoading(true);
		this.carregandoRelatorio = true;

		const postosIds = this.postos.map(posto => posto.id);

		this.tituloRelatorio = `${
			TipoTabela.PRECIPITACAO_DIARIA
		} (mm) - ${this.getPeriodo()}`;
		this.relatoriosService
			.buscarRelatorioPluviometricos(
				postosIds,
				formatISO(new Date(dataInicio)),
				formatISO(new Date(dataFim))
			)
			.subscribe({
				next: pluviometricos => {
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						pluviometricos
					);
					this.dadosTabelaRelatorio =
						this.converteFormatoRelatorioPluviometrico(pluviometricos);
				},
				error: () => {
					this.carregandoRelatorio = false;
					this.toastr.error('Ocorreu um erro ao gerar o relatório');
					this.inputs.setLoading(false);
				},
				complete: () => {
					this.carregandoRelatorio = false;
					this.inputs.setLoading(false);
				},
			});
	}

	converteFormatoRelatorioPluviometrico(dadosOriginais: any) {
		const dadosTransformados: Relatorios<DadosRelatorioPluviometricoDiario>[] =
			[];

		dadosOriginais?.relatoriosDiarios.forEach((relatorio: any) => {
			const detalhesPosto = relatorio.detalhesPosto;

			for (let i = 0; i < relatorio.dadosMensais.length; i++) {
				const dadosMensais = relatorio.dadosMensais[i];

				if (dadosMensais) {
					const municipio = detalhesPosto.municipio;
					const posto = detalhesPosto.posto;
					const tipo =
						detalhesPosto.tipo === 'PLUVIOMETRO_CONVENCIONAL'
							? 'Pluviômetro convencional'
							: detalhesPosto.tipo;
					const periodo = new Date(dadosMensais.periodo);
					const mesAno = `${
						periodo.getUTCMonth() + 1
					}/${periodo.getFullYear()}`;
					const mesAnoCompacto = this.transformarFormatoMesAno(mesAno);

					const municipioExistente = dadosTransformados.find(
						dados => dados.municipio === municipio
					);

					let data: Relatorios<DadosRelatorioPluviometricoDiario> = {
						municipio: municipio,
						data: [],
					};

					if (municipioExistente) {
						data = municipioExistente;
					}

					const dadosPosto = {
						posto,
						mesAno: mesAnoCompacto,
						tipo,
						latitude: detalhesPosto.latitude,
						longitude: detalhesPosto.longitude,
						altitude: detalhesPosto.altitude,
						microrregiao: detalhesPosto.microrregiao || '-',
						mesorregiao: detalhesPosto.mesorregiao || '-',
						regiao: detalhesPosto.regiaoPluviometrica || detalhesPosto.regiao || '-',
						bacia: detalhesPosto.bacia || '-',
						...Object.fromEntries(
							Array.from({ length: 31 }, (_, i) => [`d${i + 1}`, null])
						),
						total: dadosMensais.precipitacaoAcumulada,
					};

					data.data.push(dadosPosto);

					data.data.forEach(() => {
						dadosMensais.medicaoDiaria.forEach((md: any, index: any) => {
							if (md) {
								const seq = 'd' + (index + 1);

								data.data[data.data.length - 1][seq] = md.precipitacao;
							}
						});
					});

					if (!municipioExistente) {
						dadosTransformados.push(data);
					}
				}
			}
		});

		dadosTransformados.sort((a, b) => {
			return a.municipio.localeCompare(b.municipio, 'pt-BR', {
				sensitivity: 'base',
			});
		});

		return dadosTransformados;
	}

	transformarFormatoMesAno(stringOriginal: string) {
		const mesesAbreviados: any = {
			1: '01',
			2: '02',
			3: '03',
			4: '04',
			5: '05',
			6: '06',
			7: '07',
			8: '08',
			9: '09',
			10: '10',
			11: '11',
			12: '12',
		};

		const partes = stringOriginal.split('/');

		const mesCompleto = partes[0].toLowerCase();
		const ano = partes[1];

		const mesAbreviado = mesesAbreviados[mesCompleto];

		if (mesAbreviado) {
			return `${mesAbreviado}/${ano}`;
		} else {
			return stringOriginal;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.POSTO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
	}

	getPeriodo() {
		const dataInicio = this.getFormItemValue('dataInicio');
		const dataFim = this.getFormItemValue('dataFim');
		return DateTimeUtils.formatarDataPeriodo(dataInicio, dataFim, 'diario');
	}

	retornarPrecipitacaoDiariaParaXLSX() {
		const tableData: any[] = [];

		this.dadosTabelaRelatorio.forEach(dadoRelatorio => {
			dadoRelatorio.data.forEach(dado => {
				const municipioPosto = corrigeDuplicacaoNome(
					`${dadoRelatorio.municipio}/${dado.posto}`
				);
				const dias = Array.from({ length: 31 }, (_, i) => [
					`d${i + 1}`,
					getTracoIfNull(dado[`d${i + 1}`]),
				]);
				tableData.push({
					municipioPosto,
					latitude: renderCoordenada(dado['latitude']?.toString()),
					longitude: renderCoordenada(dado['longitude']?.toString()),
					altitude: renderCoordenada(dado['altitude']?.toString(), 1),
					microrregiao: dado['microrregiao'],
					mesorregiao: dado['mesorregiao'],
					regiao: dado['regiao'],
					bacia: dado['bacia'],
					mesAno: dado.mesAno,
					...Object.fromEntries(dias),
					total: getTracoIfNull(dado.total),
				});
			});
		});

		return tableData;
	}

	async exportarPDF(
		dadosTabelaPluviometricoDiario: Relatorios<DadosRelatorioPluviometricoDiario>[]
	) {
		const documentDefinition: any = await pdfseira.documentDefinitions(
			'landscape',
			'A3'
		);

		documentDefinition.content.push({
			text: this.tituloRelatorio,
			fontSize: 12,
			alignment: 'center',
			margin: [0, 10],
		});

		if (!dadosTabelaPluviometricoDiario.length) {
			documentDefinition.content.push({
				text: 'Nenhum dado encontrado na tabela',
				alignment: 'center',
				fontSize: 10,
				margin: [0, 10],
			});
			baixarEAbrirPDF(this.tituloRelatorio, documentDefinition);
			return;
		}

		const tableData: (string | number)[][] = [];
		const dias = Array.from({ length: 31 }, (_, i) => i + 1);

		const colunasPersonalizadasHeader = [];
		if (this.colunasSelecionadas.includes('Latitude')) colunasPersonalizadasHeader.push('Latitude');
		if (this.colunasSelecionadas.includes('Longitude')) colunasPersonalizadasHeader.push('Longitude');
		if (this.colunasSelecionadas.includes('Altitude')) colunasPersonalizadasHeader.push('Altitude');
		if (this.colunasSelecionadas.includes('Microrregião')) colunasPersonalizadasHeader.push('Microrregião');
		if (this.colunasSelecionadas.includes('Mesorregião')) colunasPersonalizadasHeader.push('Mesorregião');
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) colunasPersonalizadasHeader.push('Região Pluviométrica');
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) colunasPersonalizadasHeader.push('Bacia/Sub-bacia');

		tableData.push([
			'Município/Posto',
			...colunasPersonalizadasHeader,
			'Data',
			...dias.map(d => String(d).padStart(2, '0')),
			'Total',
		]);

		const formatarValor = (valor: any) =>
			Number(valor) > 0 ? numberToBrNumber(valor, 1) : '-';

		dadosTabelaPluviometricoDiario.forEach(dadoRelatorio => {
			dadoRelatorio.data.forEach(dado => {
				const nomePosto = corrigeDuplicacaoNome(
					`${dadoRelatorio.municipio}/${dado.posto}`
				);

				const linhaDias = dias.map(d => formatarValor(dado[`d${d}`]));

				const colunasPersonalizadasLinha = [];
				if (this.colunasSelecionadas.includes('Latitude')) {
					colunasPersonalizadasLinha.push(renderCoordenada(dado['latitude']?.toString()));
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					colunasPersonalizadasLinha.push(renderCoordenada(dado['longitude']?.toString()));
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					colunasPersonalizadasLinha.push(renderCoordenada(dado['altitude']?.toString(), 1));
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					colunasPersonalizadasLinha.push(dado['microrregiao']?.toString() || '-');
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					colunasPersonalizadasLinha.push(dado['mesorregiao']?.toString() || '-');
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					colunasPersonalizadasLinha.push(dado['regiao']?.toString() || '-');
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					colunasPersonalizadasLinha.push(dado['bacia']?.toString() || '-');
				}

				tableData.push([
					nomePosto,
					...colunasPersonalizadasLinha,
					dado.mesAno,
					...linhaDias,
					formatarValor(dado.total),
				]);
			});
		});

		const numCols = tableData[0].length;
		let fontSize = 8.5;
		let padding = 2;
		if (numCols > 38) {
			fontSize = 6.5;
			padding = 1;
		} else if (numCols > 35) {
			fontSize = 7.5;
			padding = 1.5;
		} else if (numCols > 33) {
			fontSize = 8;
		}

		const widths = tableData[0].map(colHeader => {
			const name = String(colHeader);
			if (numCols > 38) {
				if (name === 'Município/Posto') return 57;
				if (name === 'Data') return 32;
				if (name === 'Total') return 22;
				if (name === 'Latitude') return 32;
				if (name === 'Longitude') return 32;
				if (name === 'Altitude') return 27;
				if (name === 'Microrregião') return 42;
				if (name === 'Mesorregião') return 42;
				if (name === 'Região Pluviométrica') return 46;
				if (name === 'Bacia/Sub-bacia') return 46;
			} else if (numCols > 35) {
				if (name === 'Município/Posto') return 66;
				if (name === 'Data') return 35;
				if (name === 'Total') return 25;
				if (name === 'Latitude') return 36;
				if (name === 'Longitude') return 36;
				if (name === 'Altitude') return 29;
				if (name === 'Microrregião') return 48;
				if (name === 'Mesorregião') return 48;
				if (name === 'Região Pluviométrica') return 52;
				if (name === 'Bacia/Sub-bacia') return 52;
			} else {
				if (name === 'Município/Posto') return 86;
				if (name === 'Data') return 50;
				if (name === 'Total') return 35;
				if (name === 'Latitude') return 48;
				if (name === 'Longitude') return 48;
				if (name === 'Altitude') return 39;
				if (name === 'Microrregião') return 65;
				if (name === 'Mesorregião') return 65;
				if (name === 'Região Pluviométrica') return 70;
				if (name === 'Bacia/Sub-bacia') return 70;
			}
			return '*';
		});

		documentDefinition.content.push({
			table: {
				headerRows: 1,
				widths: widths,
				body: tableData.map((row, rowIndex) =>
					row.map((cell, colIndex) => ({
						text: cell,
						fontSize: fontSize,
						alignment: rowIndex > 0 && colIndex === 0 ? 'left' : 'center',
					}))
				),
			},
			layout: {
				fillColor: (rowIndex: number) => {
					return rowIndex === 0 ? '#DCDCDC' : null;
				},
				paddingLeft: () => padding,
				paddingRight: () => padding,
				paddingTop: () => padding,
				paddingBottom: () => padding,
			},
		});

		baixarEAbrirPDF(this.tituloRelatorio, documentDefinition);
	}

	exportarCSV(
		dadosTabelaPluviometricoDiario: Relatorios<DadosRelatorioPluviometricoDiario>[]
	) {
		const tableData: (string | number)[][] = [];

		const dias = Array.from({ length: 31 }, (_, i) => String(i + 1));
		
		const headerRow = ['Município/Posto'];
		if (this.colunasSelecionadas.includes('Latitude')) headerRow.push('Latitude');
		if (this.colunasSelecionadas.includes('Longitude')) headerRow.push('Longitude');
		if (this.colunasSelecionadas.includes('Altitude')) headerRow.push('Altitude');
		if (this.colunasSelecionadas.includes('Microrregião')) headerRow.push('Microrregião');
		if (this.colunasSelecionadas.includes('Mesorregião')) headerRow.push('Mesorregião');
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) headerRow.push('Região Pluviométrica');
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) headerRow.push('Bacia/Sub-bacia');
		headerRow.push('Data', ...dias, 'Total');

		tableData.push(headerRow);

		dadosTabelaPluviometricoDiario.forEach(dadoRelatorio => {
			dadoRelatorio.data.forEach(dado => {
				const formatar = (v: any) =>
					Number(v) > 0 ? Number(v).toFixed(1).replace('.', ',') : '-';
				const dias = Array.from({ length: 31 }, (_, i) =>
					formatar(dado[`d${i + 1}`])
				);
				
				const dataRow: (string | number)[] = [corrigeDuplicacaoNome(`${dadoRelatorio.municipio}/${dado.posto}`)];
				if (this.colunasSelecionadas.includes('Latitude')) {
					dataRow.push(renderCoordenada(dado['latitude']?.toString()));
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					dataRow.push(renderCoordenada(dado['longitude']?.toString()));
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					dataRow.push(renderCoordenada(dado['altitude']?.toString(), 1));
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					dataRow.push(String(dado['microrregiao'] || '-'));
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					dataRow.push(String(dado['mesorregiao'] || '-'));
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					dataRow.push(String(dado['regiao'] || '-'));
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					dataRow.push(String(dado['bacia'] || '-'));
				}
				dataRow.push(dado.mesAno, ...dias, formatar(dado.total));
				
				tableData.push(dataRow);
			});
		});

		DocumentExporter.gerarCSV(tableData, this.tituloRelatorio);
	}

	exportarTXT(
		dadosTabelaPluviometricoDiario: Relatorios<DadosRelatorioPluviometricoDiario>[]
	) {
		const formatar = (v: any) =>
			Number(v) > 0 ? Number(v).toFixed(1).replace('.', ',') : '-';

		let txtData = '';

		dadosTabelaPluviometricoDiario.forEach(dadoRelatorio => {
			const municipio = dadoRelatorio.municipio;

			dadoRelatorio.data.forEach(dado => {
				const nome = corrigeDuplicacaoNome(`${municipio}/${dado.posto}`);
				const dias = Array.from({ length: 31 }, (_, i) => {
					const dia = i + 1;
					return `${dia}: ${formatar(dado[`d${dia}`])}`;
				}).join('\n');

				let colunasPersonalizadasTxt = '';
				if (this.colunasSelecionadas.includes('Latitude')) {
					colunasPersonalizadasTxt += `Latitude: ${renderCoordenada(dado['latitude']?.toString())}\n`;
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					colunasPersonalizadasTxt += `Longitude: ${renderCoordenada(dado['longitude']?.toString())}\n`;
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					colunasPersonalizadasTxt += `Altitude: ${renderCoordenada(dado['altitude']?.toString(), 1)}\n`;
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					colunasPersonalizadasTxt += `Microrregião: ${String(dado['microrregiao'] || '-')}\n`;
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					colunasPersonalizadasTxt += `Mesorregião: ${String(dado['mesorregiao'] || '-')}\n`;
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					colunasPersonalizadasTxt += `Região Pluviométrica: ${String(dado['regiao'] || '-')}\n`;
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					colunasPersonalizadasTxt += `Bacia/Sub-bacia: ${String(dado['bacia'] || '-')}\n`;
				}

				txtData +=
					`Município/Posto: ${nome}\n` +
					colunasPersonalizadasTxt +
					`Data: ${dado.mesAno}\n` +
					`${dias}\n` +
					`Total: ${formatar(dado.total)}\n\n`;
			});
		});

		DocumentExporter.gerarTXT(txtData, this.tituloRelatorio);
	}

	exportarXLSX() {
		const nomeArquivo = this.tituloRelatorio;
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleDecimal1 = {
			...rowStyleCenter,
			format: 'num_sep_1',
		};
		const rowStyleLeft = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'left',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const headers: any[] = [
			{ label: 'municipioPosto', text: 'Município/Posto', size: 24 }
		];
		if (this.colunasSelecionadas.includes('Latitude')) {
			headers.push({ label: 'latitude', text: 'Latitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Longitude')) {
			headers.push({ label: 'longitude', text: 'Longitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Altitude')) {
			headers.push({ label: 'altitude', text: 'Altitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Microrregião')) {
			headers.push({ label: 'microrregiao', text: 'Microrregião', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Mesorregião')) {
			headers.push({ label: 'mesorregiao', text: 'Mesorregião', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
			headers.push({ label: 'regiao', text: 'Região Pluviométrica', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
			headers.push({ label: 'bacia', text: 'Bacia/Sub-bacia', size: 20 });
		}
		headers.push(
			{ label: 'mesAno', text: 'Data', size: 24 },
			...Array.from({ length: 31 }, (_, i) => ({
				label: `d${i + 1}`,
				text: `d${i + 1}`,
				size: 10,
				numFmt: '0.0',
			})),
			{ label: 'total', text: 'Total', size: 10, numFmt: '0.0' }
		);

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyleCenter,
				rowStyleDecimal1,
				rowStyleLeft,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						}

						const label = headers[colIndex]?.label;
						if (label === 'municipioPosto' || label === 'microrregiao' || label === 'mesorregiao' || label === 'regiao' || label === 'bacia') {
							return 'rowStyleLeft';
						}

						if (label?.startsWith('d') || label === 'total' || label === 'latitude' || label === 'longitude' || label === 'altitude') {
							return 'rowStyleDecimal1';
						}

						return 'rowStyleCenter';
					},
					headers: headers,
					data: this.retornarPrecipitacaoDiariaParaXLSX(),
					convertStringToNumber: true,
					columns: [],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.POSTO)?.clearValidators();

		this.subscription.unsubscribe();
	}
}
